import React, { useState, useEffect } from 'react';

const XGroupForm = ({ initialData, isEditing, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    x_user_ids: []
  });
  const [userIdInput, setUserIdInput] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        x_user_ids: initialData.x_user_ids || []
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Group name must be 100 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (err) {
      console.error('Error saving group:', err);
      alert('Failed to save group: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddUserId = () => {
    const trimmedId = userIdInput.trim();
    if (trimmedId && !formData.x_user_ids.includes(trimmedId)) {
      setFormData({
        ...formData,
        x_user_ids: [...formData.x_user_ids, trimmedId]
      });
      setUserIdInput('');
    }
  };

  const handleRemoveUserId = (userId) => {
    setFormData({
      ...formData,
      x_user_ids: formData.x_user_ids.filter(id => id !== userId)
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddUserId();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label htmlFor="name" className="form-label">
          Group Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Finance Influencers"
          maxLength={100}
        />
        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        <small className="form-text text-muted">
          A descriptive name for this group of X users
        </small>
      </div>

      <div className="mb-3">
        <label htmlFor="userIdInput" className="form-label">
          X Users
        </label>
        <div className="input-group mb-2">
          <input
            type="text"
            className="form-control"
            id="userIdInput"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter @handle, username, or user ID (e.g., @jack or 12)"
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleAddUserId}
            disabled={!userIdInput.trim()}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add
          </button>
        </div>
        <small className="form-text text-muted">
          Add one @handle, username, or numeric user ID at a time. Press Enter or click Add.
        </small>
      </div>

      {formData.x_user_ids.length > 0 && (
        <div className="mb-3">
          <label className="form-label">Current Users ({formData.x_user_ids.length})</label>
          <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {formData.x_user_ids.map((userId, index) => (
              <div
                key={index}
                className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
              >
                <span className="font-monospace">{userId}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRemoveUserId(userId)}
                  title="Remove user ID"
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mt-4">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2"></i>
              {isEditing ? 'Update Group' : 'Create Group'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default XGroupForm;
